/**
 * i18n bootstrap.
 *
 * Phase 10 wired the canonical resource bundles into i18next. Phase 13
 * makes the locale bundles lazy: only the requested locale's JSON is
 * fetched (plus the fallback `en-GB` so missing keys never render
 * blank). This drops ~30kB from the initial bundle for any tenant whose
 * locale isn't `en-GB`.
 *
 * Three bundles ship today:
 *
 *   - en-GB → canonical, fallback for any unknown locale.
 *   - sv-SE → Hemmaklubben copy (the source surface).
 *   - da-DK → Danish translation.
 *
 * If `initI18n(locale)` is called with a locale we have no bundle for,
 * we surface a `i18n-locale-fallback` event via the observability
 * pipeline and fall back to `en-GB`. Subsequent `changeLanguage` calls
 * follow the same rule.
 */

import i18next, { type i18n as I18nInstance } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next, useTranslation as useTranslationBase } from 'react-i18next';
import { reportEvent } from '../observability.js';

const FALLBACK_LOCALE = 'en-GB';

/**
 * Lazy locale loader. Each `import()` call is split into its own chunk
 * by Vite (one per locale JSON), so the bundle ships only the locale
 * the tenant actually uses + the en-GB fallback.
 */
const LOCALE_LOADERS: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  'en-GB': () => import('./locales/en-GB.json'),
  'sv-SE': () => import('./locales/sv-SE.json'),
  'da-DK': () => import('./locales/da-DK.json'),
};

const SUPPORTED_LOCALES: ReadonlySet<string> = new Set(Object.keys(LOCALE_LOADERS));

let initPromise: Promise<I18nInstance> | null = null;
const loadedLocales = new Set<string>();

/**
 * Resolve a tenant-supplied locale to one of our shipped bundles.
 * Records a fallback event if the bundle is missing so dashboards can
 * spot tenants we should localise next.
 */
function resolveLocale(requested: string): string {
  if (SUPPORTED_LOCALES.has(requested)) {
    return requested;
  }
  reportEvent('i18n-locale-fallback', {
    requested,
    fallback: FALLBACK_LOCALE,
  });
  return FALLBACK_LOCALE;
}

/**
 * Fetch a locale bundle and register it with i18next. Idempotent — a
 * second call for the same locale resolves immediately without
 * re-downloading.
 *
 * Note: i18next instance methods like `addResourceBundle` only exist
 * AFTER `.init()` has been called. Callers must check `initialized`
 * first or use `loadLocaleModule` (returns the raw bundle) and feed it
 * into the init() resources field for first-call seeding.
 */
async function loadLocaleResources(locale: string): Promise<void> {
  if (loadedLocales.has(locale)) return;
  const loader = LOCALE_LOADERS[locale];
  if (!loader) return;
  const mod = await loader();
  // i18next.addResourceBundle is synchronous; only the dynamic import is async.
  i18next.addResourceBundle(locale, 'translation', mod.default, true, true);
  loadedLocales.add(locale);
}

/** Load a locale module without touching i18next — used during init(). */
async function loadLocaleModule(
  locale: string,
): Promise<Record<string, unknown> | null> {
  const loader = LOCALE_LOADERS[locale];
  if (!loader) return null;
  const mod = await loader();
  return mod.default;
}

/**
 * Un-flatten the dotted-key catalog (`cms.ui_strings`, e.g.
 * `{ 'lobby.readTime_one': '…', 'chip.yes': 'Yes' }`) into the nested
 * structure i18next expects (`{ lobby: { readTime_one: '…' }, chip: {
 * yes: 'Yes' } }`). Our keys are leaf-only, so no node/leaf collisions.
 */
function unflatten(flat: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.');
    let node = out;
    for (let i = 0; i < parts.length - 1; i += 1) {
      const p = parts[i] as string;
      if (typeof node[p] !== 'object' || node[p] === null) {
        node[p] = {};
      }
      node = node[p] as Record<string, unknown>;
    }
    node[parts[parts.length - 1] as string] = value;
  }
  return out;
}

/**
 * Overlay the DB-served catalog onto a locale's i18next bundle, AFTER
 * init(). `deep + overwrite` so the catalog wins over the static
 * bootstrap bundle. The catalog shipped in `RuntimeConfig.i18n.strings`
 * is already locale-resolved with en-GB fallback baked in by the engine,
 * so every key resolves — the static bundle is only an offline safety
 * net for the very first paint before `/config` lands.
 */
function applyCatalog(locale: string, catalog: Record<string, string>): void {
  if (!i18next.isInitialized) return;
  i18next.addResourceBundle(locale, 'translation', unflatten(catalog), true, true);
  loadedLocales.add(locale);
}

/**
 * Initialise i18next with the tenant's locale. Idempotent — subsequent
 * calls switch the locale without re-creating the instance, lazily
 * downloading the locale bundle on first use.
 */
export async function initI18n(
  initialLocale: string,
  catalog?: Record<string, string>,
): Promise<I18nInstance> {
  // With a catalog (the DB-served single source of truth) we honour ANY
  // locale — the engine already baked en-GB fallback into every key, so a
  // brand-new output locale needs no static bundle and no code change.
  // Without a catalog (bootstrap / tests) we resolve to a shipped static
  // bundle and surface a fallback event for unknown locales.
  const resolved = catalog ? initialLocale : resolveLocale(initialLocale);

  if (initPromise) {
    const instance = await initPromise;
    if (!catalog && resolved !== FALLBACK_LOCALE) {
      await loadLocaleResources(resolved);
    }
    if (catalog) {
      applyCatalog(resolved, catalog);
    }
    if (instance.language !== resolved) {
      await instance.changeLanguage(resolved);
    }
    return instance;
  }

  initPromise = (async (): Promise<I18nInstance> => {
    // Pre-load the fallback bundle and (if different) the resolved bundle
    // BEFORE calling init(). Instance methods like `addResourceBundle` are
    // not available on i18next until after init() has run, so we feed the
    // bundles in via the init() resources field instead.
    const resources: Record<string, { translation: Record<string, unknown> }> = {};
    const fallbackBundle = await loadLocaleModule(FALLBACK_LOCALE);
    if (fallbackBundle) {
      resources[FALLBACK_LOCALE] = { translation: fallbackBundle };
      loadedLocales.add(FALLBACK_LOCALE);
    }
    if (!catalog && resolved !== FALLBACK_LOCALE) {
      const resolvedBundle = await loadLocaleModule(resolved);
      if (resolvedBundle) {
        resources[resolved] = { translation: resolvedBundle };
        loadedLocales.add(resolved);
      }
    }
    if (catalog) {
      // The DB catalog IS the source of truth for the active locale; it
      // overlays (and wins over) the static en-GB bootstrap bundle.
      resources[resolved] = { translation: unflatten(catalog) };
      loadedLocales.add(resolved);
    }
    await i18next
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        lng: resolved,
        fallbackLng: FALLBACK_LOCALE,
        // With a catalog any locale is valid (DB-driven); without one we
        // restrict detection to the shipped static bundles.
        supportedLngs: catalog ? false : Object.keys(LOCALE_LOADERS),
        resources,
        interpolation: { escapeValue: false }, // React already escapes
        detection: {
          // Prefer explicit locale from tenant config; only fall back to
          // browser detection if `initI18n` is called without a locale.
          order: ['htmlTag', 'navigator'],
          caches: [],
        },
        // Don't crash on a missing key in a half-built surface.
        returnNull: false,
        returnEmptyString: false,
      });
    return i18next;
  })();

  return initPromise;
}

/**
 * Re-export `useTranslation` so consumers get the typed hook from a
 * single location and we can swap implementations later if needed.
 */
export const useTranslation = useTranslationBase;

export { i18next };
